import { ChangeDetectionStrategy, Component, inject, OnDestroy, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayersTypes } from '@app/core/shared/enums/field-view-layer.types';
import { DataTransferService } from '@app/core/shared/services/data-transfer.service';
import { ClarityService } from '@services/clarity.service';
import { MaterialSymbol } from 'material-symbols';
import { Units } from 'ol/control/ScaleLine';
import { firstValueFrom } from 'rxjs';
import { FieldViewControlService } from '../../services/field-view-control.service';
import { FieldViewStateService } from '../../services/field-view-state.service';
import ExportUtility from '../../utilities/export.utility';
import MapLayerUtility from '../../utilities/map-layer.utility';

const materialDs = [
  MatCheckboxModule,
  MatFormFieldModule,
  MatSelectModule,
  MatButtonModule,
  MatIconModule,
  MatInputModule,
  MatTooltipModule
];
@Component({
  selector: 'app-control-panel',
  templateUrl: './control-panel.component.html',
  styleUrls: ['./control-panel.component.scss'],
  imports: [...materialDs],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ControlPanelComponent implements OnDestroy {
  debounceTimer = null;

  private readonly fieldViewControlService = inject(FieldViewControlService);
  private readonly fieldViewStateService = inject(FieldViewStateService);
  private readonly dataTransferService = inject(DataTransferService);
  private readonly clarityService = inject(ClarityService);
  private readonly matIconReg = inject(MatIconRegistry);

  readonly graticuleVisibilitySignal = this.fieldViewControlService.graticuleVisibility;
  readonly kpSpacingSignal = this.fieldViewControlService.kpSpacing;
  readonly mapScaleUnitSignal = this.fieldViewControlService.mapScaleUnit;
  readonly kpActionStateSignal = this.fieldViewControlService.kpActionButtonState;
  readonly routeDataSignal = this.fieldViewStateService.routeData;

  readonly fileDownloadIcon: MaterialSymbol = 'file_save';
  readonly arrowForwardIcon: MaterialSymbol = 'arrow_forward';
  readonly arrowBackIcon: MaterialSymbol = 'arrow_back';
  readonly zoomInMapIcon: MaterialSymbol = 'zoom_in_map';

  readonly MapScaleOptions = [
    { value: 'metric', text: 'Metric' },
    { value: 'imperial', text: 'Imperial' },
    { value: 'nautical', text: 'Nautical Mile' }
  ];

  constructor() {
    this.clarityService.initializeClarity();
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
  }

  updateGraticule(value: boolean): void {
    this.clarityService.clarityEvent(`field-view:graticule-check-${value}`);
    this.fieldViewControlService.setGraticuleVisibility(value);
  }

  updateKpSpacing(value: number): void {
    this.clarityService.clarityEvent('field-view:kp-spacing-input');
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (value >= 0) {
        this.fieldViewControlService.setKpSpacing(Number(value));
      }
    }, 1000);
  }

  updateMapScale(value: Units): void {
    this.clarityService.clarityEvent(`field-view:map-units-select-${value}`);
    this.fieldViewControlService.setMapScaleUnit(value);
  }

  kpActionButton(navigation: '' | 'prev' | 'next' | 'first'): void {
    this.clarityService.clarityEvent(`field-view:kp-${navigation}-btn`);
    this.fieldViewControlService.setKpActionButtonState({ ...this.kpActionStateSignal(), navigation });
  }

  exportGeoJson(): void {
    this.clarityService.clarityEvent(`field-view:export-route-geojson-btn`);
    untracked(async () => {
      const pipeline = await firstValueFrom(this.dataTransferService.currentInstallation);
      const layer = MapLayerUtility.getVectorLayerFromMap(
        this.fieldViewStateService.fieldViewMapState(),
        'layerType',
        LayersTypes.ROUTE_DATA_LAYER
      );

      ExportUtility.exportLayersToGeoJSON(layer, `${pipeline.name}_${LayersTypes.ROUTE_DATA_LAYER}.geojson`);
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.debounceTimer);
  }
}
